import { Controller } from "@hotwired/stimulus"

// Scene-router controller.
//
// Lives on a single <div class="scene-stage"> in the public layout, but its
// real job is to coordinate multiple Three.js scenes anchored to specific
// elements on the page (each marked with `data-scene="globe"` or
// `data-scene="gallery"` etc.). For each host element, a SceneModule is
// instantiated. The router runs ONE shared WebGLRenderer; each frame the
// scene whose host element is most-on-screen is rendered into the canvas
// that overlays its host.
//
// Key rules (enforced here, not by individual modules):
//   - THREE is dynamically imported so the hero's first paint doesn't pay
//     for the Three.js bundle.
//   - Modules are mounted only when their host enters within 1.5 viewports,
//     and unmounted when more than 2 viewports away.
//   - All animation is scroll-bound or interaction-bound. The router does
//     NOT autoplay a render loop when nothing is on screen — it only renders
//     when at least one module is active AND its host is visible.
//   - prefers-reduced-motion + mobile (<768px) → router refuses to mount
//     ANY module; the host element falls back to its HTML alternative.

const ROUTES = {
  // Lazy imports — the module file is only fetched when first needed.
  globe:   () => import("../scene/indonesia_globe_scene"),
  gallery: () => import("../scene/device_gallery_scene"),
  stack:   () => import("../scene/stack_lattice_scene")
}

const MOUNT_BAND_VH    = 1.5  // mount when host within 1.5 viewport heights
const UNMOUNT_BAND_VH  = 2.0  // unmount when host more than 2 viewport heights
const MOBILE_BREAKPOINT = 768

export default class extends Controller {
  connect() {
    this._reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this._isMobile      = window.innerWidth < MOBILE_BREAKPOINT
    this.element.classList.toggle("scene--disabled", this._reducedMotion || this._isMobile)
    if (this._reducedMotion || this._isMobile) return

    // Find host elements once.
    this._hosts = Array.from(document.querySelectorAll("[data-scene]"))
    if (!this._hosts.length) return

    // Each host → state object. THREE itself is NOT imported here — we
    // defer the heavy chunk (THREE + scene modules + texture loader) until
    // the first scene actually enters its mount band. That keeps the
    // marketing hero's critical path free of the 600 KB-ish scene chunk.
    this._states = this._hosts.map(host => ({
      host,
      type: host.dataset.scene,
      module: null,
      status: "idle"  // 'idle' | 'mounting' | 'mounted' | 'unmounting'
    }))

    this._heavyBooted = false
    this._bootPromise = null

    // Bind only the scroll listener now; full event wiring + renderer
    // creation runs lazily in _bootHeavy() the first time a host enters.
    this._onScrollFromWindow = () => this._wakeUp()
    window.addEventListener("scroll", this._onScrollFromWindow, { passive: true })
    if (window.__lenis) {
      this._lenisListener = () => this._wakeUp()
      window.__lenis.on("scroll", this._lenisListener)
    }
    this._wakeUp()  // initial probe — fires _bootHeavy if a host is already near
  }

  async _bootHeavy() {
    if (this._heavyBooted) return
    if (this._bootPromise) return this._bootPromise
    this._bootPromise = (async () => {
      this._THREE = await import("three")
      if (!this._connected) return
      this._buildRenderer()
      const { TextureLoader } = await import("../scene/texture_loader")
      this._textureLoader = new TextureLoader(this._THREE, this._renderer)
      this._bindHeavyEvents()
      this._clock = new this._THREE.Clock()
      this._tick  = this._tick.bind(this)
      this._rafActive = false
      this._heavyBooted = true
    })()
    await this._bootPromise
  }

  initialize() { this._connected = true }
  disconnect() {
    this._connected = false
    this._teardown()
  }

  _buildRenderer() {
    const THREE = this._THREE
    this._renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: "high-performance"
    })
    this._renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5))
    this._renderer.setSize(window.innerWidth, window.innerHeight)
    this._renderer.setClearColor(0x000000, 0)
    this._renderer.outputColorSpace = THREE.SRGBColorSpace
    this.element.appendChild(this._renderer.domElement)
  }

  _bindHeavyEvents() {
    this._onResize = () => {
      const w = window.innerWidth
      const h = window.innerHeight
      this._renderer.setSize(w, h)
      this._states.forEach(s => s.module?.resize?.(w, h))
      // Re-check mobile transition.
      const wasMobile = this._isMobile
      this._isMobile = w < MOBILE_BREAKPOINT
      if (this._isMobile && !wasMobile) {
        this.element.classList.add("scene--disabled")
        this._teardown()
      } else if (!this._isMobile && wasMobile) {
        this.element.classList.remove("scene--disabled")
        // re-mount path is rare; reload-friendly behaviour is acceptable.
      }
    }
    window.addEventListener("resize", this._onResize, { passive: true })

    this._onVisibility = () => {
      if (!document.hidden) this._wakeUp()
    }
    document.addEventListener("visibilitychange", this._onVisibility)
  }

  _teardown() {
    if (this._rafId) cancelAnimationFrame(this._rafId)
    this._rafId = null
    this._rafActive = false
    window.removeEventListener("resize", this._onResize)
    window.removeEventListener("scroll", this._onScrollFromWindow)
    document.removeEventListener("visibilitychange", this._onVisibility)
    if (window.__lenis && this._lenisListener) window.__lenis.off("scroll", this._lenisListener)

    this._states?.forEach(s => {
      if (s.module) {
        s.module.unmount()
        s.module = null
        s.status = "idle"
      }
    })
    if (this._renderer) {
      this._renderer.dispose()
      this._renderer.domElement.remove()
      this._renderer = null
    }
    if (this._textureLoader) {
      this._textureLoader.disposeAll()
      this._textureLoader = null
    }
  }

  _wakeUp() {
    if (!this._states) return
    const viewportH = window.innerHeight
    let anyVisible = false
    let anyInMountBand = false

    for (const state of this._states) {
      const rect = state.host.getBoundingClientRect()
      const dist = this._distanceFromViewport(rect, viewportH)
      const visible = rect.bottom > 0 && rect.top < viewportH

      if (visible) anyVisible = true
      if (dist <= MOUNT_BAND_VH) anyInMountBand = true
    }

    // Defer THREE + heavy chunk load until at least one host is within the
    // mount band. After boot, normal mount/unmount logic runs in the same
    // wake-up cycle (via _bootHeavy().then()).
    if (anyInMountBand && !this._heavyBooted) {
      this._bootHeavy().then(() => this._wakeUp())
      return
    }
    if (!this._heavyBooted) return

    for (const state of this._states) {
      const rect = state.host.getBoundingClientRect()
      const dist = this._distanceFromViewport(rect, viewportH)
      if (dist <= MOUNT_BAND_VH && state.status === "idle") {
        this._mount(state)
      } else if (dist > UNMOUNT_BAND_VH && state.status === "mounted") {
        this._unmount(state)
      }
    }

    if (anyVisible && !this._rafActive && this._clock) {
      this._rafActive = true
      this._clock.getDelta()
      this._rafId = requestAnimationFrame(this._tick)
    }
  }

  // Distance from element to viewport in units of viewport heights (0 = on-screen).
  _distanceFromViewport(rect, viewportH) {
    if (rect.bottom < 0)         return -rect.bottom / viewportH
    if (rect.top   > viewportH)  return (rect.top - viewportH) / viewportH
    return 0
  }

  async _mount(state) {
    state.status = "mounting"
    const loader = ROUTES[state.type]
    if (!loader) {
      console.warn(`Unknown scene type: ${state.type}`)
      state.status = "idle"
      return
    }
    try {
      const moduleNs = await loader()
      const SceneClass = moduleNs.default
      const sceneModule = new SceneClass(state.host)
      await sceneModule.mount({
        THREE: this._THREE,
        renderer: this._renderer,
        size: { width: window.innerWidth, height: window.innerHeight },
        textureLoader: this._textureLoader
      })
      state.module = sceneModule
      state.status = "mounted"
      // Position the renderer canvas via the host's stage; the canvas itself
      // is the single full-bleed one in `.scene-stage`.
      this._wakeUp()
    } catch (err) {
      console.error(`Failed to mount scene '${state.type}':`, err)
      state.status = "idle"
    }
  }

  _unmount(state) {
    state.status = "unmounting"
    try { state.module?.unmount() } catch (e) { console.error(e) }
    state.module = null
    state.status = "idle"
  }

  _tick() {
    const dt = this._clock.getDelta()
    const viewportH = window.innerHeight
    let anyRendered = false

    for (const state of this._states) {
      if (state.status !== "mounted") continue
      const rect = state.host.getBoundingClientRect()

      // For pinned-sticky scenes, the *outer* host extends past the visible
      // area while the *inner sticky child* stays in view during the pin.
      // Use the sticky child's rect when present so we keep rendering for
      // the full pin range without bleeding into the next section.
      const stickyEl = state.host.querySelector(".scene-host__sticky")
      const aRect = stickyEl ? stickyEl.getBoundingClientRect() : rect
      const aCenter = aRect.top + aRect.height / 2
      const inActiveBand = aCenter > 0 && aCenter < viewportH
      if (!inActiveBand) continue

      // Scroll progress through the outer host (modules compute their own
      // sticky-aware progress internally; this raw 0..1 is fine as a hint).
      const total = rect.height + viewportH
      const passed = viewportH - rect.top
      const progress = Math.min(1, Math.max(0, passed / total))

      state.module.update(dt, progress)
      this._renderer.render(state.module.scene, state.module.camera)
      anyRendered = true
      break
    }

    if (anyRendered) {
      this.element.classList.add("scene-stage--active")
      this._rafId = requestAnimationFrame(this._tick)
    } else {
      // Nothing on-screen — clear the canvas (so a previously-rendered frame
      // doesn't bleed into other sections), drop the active class, then idle.
      this._renderer.clear()
      this.element.classList.remove("scene-stage--active")
      this._rafActive = false
      this._rafId = null
    }
  }
}
